import React, { useEffect, useState, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { useDarkMode, useDashboardAnalytics } from 'Hooks';
import { canViewTeamPerformance, canExportDashboardPdf } from 'Helpers/PermissionsHelper';
import { toastr } from 'Helpers/ToastHelper';
import LoadingSpinner from 'Components/LoadingSpinner';
import ErrorDisplay from 'Components/ErrorDisplay';
import F2WCustomLogger from 'Services/F2WCustomLogger';
import DashboardApiService from 'Services/ApiServices/DashboardApiService';

import SeasonFilter from './Components/SeasonFilter';
import DateRangePicker from './Components/DateRangePicker';
import SummaryCardsV2 from './Components/SummaryCardsV2';
import ConfirmedByMonthChart from './Components/ConfirmedByMonthChart';
import ChartCard from './Components/ChartCard';
import WorldMap from './Components/WorldMap';
import SourceMarketSection from './Components/SourceMarketSection';
import CnxModSection from './Components/CnxModSection';
import TeamPerformance from './Components/TeamPerformance';
import PdfExportButton from './Components/PdfExportButton';
import RefreshIndicator from './Components/RefreshIndicator';

const Dashboard = () => {
  const { darkMode } = useDarkMode();
  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles || [];
  const dashboardRef = useRef(null);

  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  const [seasonsError, setSeasonsError] = useState(null);

  // Draft dates: what the user is selecting (not yet applied)
  const [draftDateFrom, setDraftDateFrom] = useState('');
  const [draftDateTo, setDraftDateTo] = useState('');

  // Applied dates: what is actually sent to the API
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const handleApplyDates = () => {
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
  };

  const handleClearDates = () => {
    setDraftDateFrom('');
    setDraftDateTo('');
    setDateFrom('');
    setDateTo('');
  };

  // Check if draft dates differ from applied dates (show apply button as active)
  const hasPendingDateChanges = draftDateFrom !== dateFrom || draftDateTo !== dateTo;

  // Phase 1: Load available seasons
  useEffect(() => {
    let cancelled = false;
    const loadSeasons = async () => {
      try {
        setSeasonsLoading(true);
        setSeasonsError(null);
        const result = await DashboardApiService.getSeasons();
        if (cancelled) return;
        const seasons = result?.data || result || [];
        setAvailableSeasons(seasons);
        if (seasons.length > 0) {
          setSelectedSeason(seasons[seasons.length - 1]);
        }
      } catch (err) {
        if (cancelled) return;
        setSeasonsError(err);
        F2WCustomLogger.error('Failed to load seasons', err, { view: 'Dashboard' });
      } finally {
        if (!cancelled) setSeasonsLoading(false);
      }
    };
    loadSeasons();
    return () => {
      cancelled = true;
    };
  }, []);

  // Phase 2: Load analytics once season is set
  const { data, isLoading, isRefreshing, error, refetch } = useDashboardAnalytics(
    selectedSeason,
    dateFrom || null,
    dateTo || null
  );

  // Update available seasons from analytics response (keeps list fresh)
  useEffect(() => {
    if (data?.seasons?.length > 0) {
      setAvailableSeasons(data.seasons);
    }
  }, [data?.seasons]);

  useEffect(() => {
    document.title = 'F2W - Dashboard';
  }, []);

  // Log initial load errors
  useEffect(() => {
    if (error && !isRefreshing) {
      F2WCustomLogger.error('Failed to load dashboard', error, { view: 'Dashboard' });
    }
  }, [error, isRefreshing]);

  // Toast on refresh failure (when we still have data)
  useEffect(() => {
    if (error && isRefreshing) {
      toastr.error('Dashboard refresh failed', 'Displaying previously loaded data');
    }
  }, [error, isRefreshing]);

  // Initial loading state
  if (seasonsLoading || isLoading) {
    return <LoadingSpinner />;
  }

  // Seasons load failure
  if (seasonsError) {
    return <ErrorDisplay error={seasonsError} onRetry={() => window.location.reload()} />;
  }

  // Initial failure — no data available
  if (error && !data) {
    return <ErrorDisplay error={error} onRetry={() => refetch(false)} />;
  }

  const dashboardData = data || {};
  const summary = dashboardData.summary || {};
  const confirmedByMonth = dashboardData.confirmedByMonth || [];
  const countryStatus = dashboardData.countryStatus || [];
  const sourceMarket = dashboardData.sourceMarket || [];
  const flightType = dashboardData.flightType || [];
  const cnxMod = dashboardData.cnxMod || {};
  const teamPerformance = dashboardData.teamPerformance || [];

  return (
    <React.Fragment>
      <RefreshIndicator isRefreshing={isRefreshing} />

      <div ref={dashboardRef}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0 text-nowrap">
            <i className="fa fa-bar-chart me-2" />
            Flight Tickets Dashboard
          </h4>
          <div className="d-flex align-items-center gap-3">
            <SeasonFilter
              seasons={availableSeasons}
              selectedSeason={selectedSeason}
              onSeasonChange={setSelectedSeason}
            />
            <DateRangePicker
              dateFrom={draftDateFrom}
              dateTo={draftDateTo}
              onDateFromChange={setDraftDateFrom}
              onDateToChange={setDraftDateTo}
              onApply={handleApplyDates}
              onClear={handleClearDates}
              hasPendingChanges={hasPendingDateChanges}
            />
            {canExportDashboardPdf(userRoles) && (
              <PdfExportButton
                season={selectedSeason}
                userName={user?.given_name || user?.name || user?.email || ''}
                dashboardRef={dashboardRef}
                userRoles={userRoles}
              />
            )}
          </div>
        </div>

        <SummaryCardsV2 summary={summary} />

        <Row>
          <Col lg={6}>
            <ChartCard
              id="chart-confirmed-by-month"
              title="Confirmed Flights by Month"
              icon="fa fa-calendar"
              headerColor="#1cc88a"
              darkMode={darkMode}
            >
              <ConfirmedByMonthChart data={confirmedByMonth} />
            </ChartCard>
          </Col>
          <Col lg={6}>
            <ChartCard
              id="chart-world-map"
              title="Flight Requests by Country"
              icon="fa fa-globe"
              headerColor="#4e73df"
              darkMode={darkMode}
            >
              <WorldMap countryStatus={countryStatus} />
            </ChartCard>
          </Col>
        </Row>

        <SourceMarketSection sourceMarket={sourceMarket} flightType={flightType} darkMode={darkMode} />

        <Row>
          <Col lg={12}>
            <ChartCard
              id="chart-cnx-mod"
              title="Cancellation & Modification Analysis"
              icon="fa fa-exclamation-triangle"
              headerColor="#e74a3b"
              darkMode={darkMode}
            >
              <CnxModSection cnxMod={cnxMod} darkMode={darkMode} />
            </ChartCard>
          </Col>
        </Row>

        {canViewTeamPerformance(userRoles) && (
          <Row>
            <Col lg={12}>
              <ChartCard
                id="chart-team-performance"
                title="Team Performance"
                icon="fa fa-users"
                headerColor="#36b9cc"
                darkMode={darkMode}
              >
                <TeamPerformance data={teamPerformance} />
              </ChartCard>
            </Col>
          </Row>
        )}
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
